import {
  CARD_HEIGHT,
  CARD_WIDTH,
  getFrames,
  getImageRect,
  getSliderClipRect,
  PHOTO_AREA_HEIGHT,
  type PhotoCompareSlotKey,
  type PhotoCompareState,
  type Rect,
} from "@fitness-app/client-shared/features/measurements/photo-compare-layout";

// TRE-2290 — canvas 2D drawing of the before / after card for the trainer panel. A trimmed port of
// apps/client-web/src/features/measurements/photo-compare/draw-photo-compare.ts: same card units
// and shared geometry, without the face covers, the logotype and the PNG export (trainers only
// look at the comparison, they do not share it).

export type PhotoCompareImages = Record<PhotoCompareSlotKey, HTMLImageElement | null>;

export interface PhotoCompareTexts {
  /** Date under each label, e.g. "12.03.2026". */
  dates: Record<PhotoCompareSlotKey, string>;
  /** "PRZED" / "PO". */
  titles: Record<PhotoCompareSlotKey, string>;
  /** Shown in an empty frame. */
  choosePhoto: string;
}

const BACKGROUND = "#1B1A17";
const PLACEHOLDER = "#2A2925";
const LABEL_MUTED = "rgba(242,240,231,0.62)";
const INK_LIGHT = "#FEFEFE";
const GOLD = "#D3BA7A";
const DIVIDER_HANDLE_RADIUS = 34;
const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

type Ctx = CanvasRenderingContext2D;

const setLetterSpacing = (ctx: Ctx, value: string) => {
  // Older Safari has no canvas letterSpacing; the labels just render a bit tighter there.
  if ("letterSpacing" in ctx) {
    (ctx as Ctx & { letterSpacing: string }).letterSpacing = value;
  }
};

const clipRect = (ctx: Ctx, rect: Rect) => {
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();
};

const drawSlot = (
  ctx: Ctx,
  images: PhotoCompareImages,
  state: PhotoCompareState,
  slot: PhotoCompareSlotKey,
  texts: PhotoCompareTexts,
) => {
  const frame = getFrames(state.mode)[slot];
  const image = images[slot];

  ctx.save();
  clipRect(ctx, frame);
  // A zoomed-out photo no longer covers its frame; the strip around it must be background, not
  // the other photo drawn underneath (slider mode stacks the two).
  ctx.fillStyle = PLACEHOLDER;
  ctx.fillRect(frame.x, frame.y, frame.width, frame.height);
  if (!image) {
    ctx.fillStyle = LABEL_MUTED;
    ctx.font = `400 36px ${FONT_FAMILY}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(texts.choosePhoto, frame.x + frame.width / 2, frame.y + frame.height / 2);
    ctx.restore();
    return;
  }

  const rect = getImageRect(frame, { width: image.naturalWidth, height: image.naturalHeight }, state[slot].adjustment);
  ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
};

const drawDivider = (ctx: Ctx, split: number) => {
  const x = split * CARD_WIDTH;
  ctx.fillStyle = INK_LIGHT;
  ctx.fillRect(x - 2, 0, 4, PHOTO_AREA_HEIGHT);

  const cy = PHOTO_AREA_HEIGHT / 2;
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.arc(x, cy + 3, DIVIDER_HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = INK_LIGHT;
  ctx.beginPath();
  ctx.arc(x, cy, DIVIDER_HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = BACKGROUND;
  ctx.lineWidth = 4;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x - 8, cy - 10);
  ctx.lineTo(x - 18, cy);
  ctx.lineTo(x - 8, cy + 10);
  ctx.moveTo(x + 8, cy - 10);
  ctx.lineTo(x + 18, cy);
  ctx.lineTo(x + 8, cy + 10);
  ctx.stroke();
};

const drawLabelBar = (ctx: Ctx, texts: PhotoCompareTexts) => {
  const top = PHOTO_AREA_HEIGHT;
  const padding = 48;
  ctx.textBaseline = "top";

  const drawLabel = (slot: PhotoCompareSlotKey, x: number, align: CanvasTextAlign) => {
    ctx.textAlign = align;
    ctx.fillStyle = GOLD;
    ctx.font = `700 30px ${FONT_FAMILY}`;
    setLetterSpacing(ctx, "5px");
    ctx.fillText(texts.titles[slot], x, top + 34);
    setLetterSpacing(ctx, "0px");
    ctx.fillStyle = INK_LIGHT;
    ctx.font = `500 32px ${FONT_FAMILY}`;
    ctx.fillText(texts.dates[slot], x, top + 34 + 40);
  };

  drawLabel("before", padding, "left");
  drawLabel("after", CARD_WIDTH - padding, "right");
};

/** Draws the whole card in card units; the caller sets the transform for the target resolution. */
export const drawPhotoCompare = (
  ctx: Ctx,
  images: PhotoCompareImages,
  state: PhotoCompareState,
  texts: PhotoCompareTexts,
) => {
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  if (state.mode === "slider") {
    drawSlot(ctx, images, state, "after", texts);
    ctx.save();
    clipRect(ctx, getSliderClipRect(state.split));
    drawSlot(ctx, images, state, "before", texts);
    ctx.restore();
    drawDivider(ctx, state.split);
  } else {
    drawSlot(ctx, images, state, "before", texts);
    drawSlot(ctx, images, state, "after", texts);
  }

  drawLabelBar(ctx, texts);
};
